import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Typography, Button, Card, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, Tooltip, Chip, Grid,
    FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const PayrollGroups = ({ setIsLoggedIn }) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    // Group Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [formData, setFormData] = useState({
        group_code: '',
        group_name: '',
        description: '',
        cutoff_type: 'CALENDAR',
        cutoff_start_day: 1,
        cutoff_end_day: 0,
        is_active: 1
    });

    // Schedule Dialog State
    const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
    const [selectedSchedulesGroup, setSelectedSchedulesGroup] = useState(null);
    const [selectedGroupForSchedule, setSelectedGroupForSchedule] = useState(null);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [scheduleFormData, setScheduleFormData] = useState({
        schedule_name: '',
        split_day: 15,
        pay_basic: 1,
        pay_ot: 1,
        pay_allowances: 1,
        run_order: 1,
        is_active: 1
    });

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll-groups`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                setGroups(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching payroll groups:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    // -----------------------------------------------------
    // Group Handlers
    // -----------------------------------------------------
    const handleOpenDialog = useCallback((group = null) => {
        if (group) {
            setEditingGroup(group);
            setFormData({
                group_code: group.group_code,
                group_name: group.group_name,
                description: group.description || '',
                cutoff_type: group.cutoff_type || 'CALENDAR',
                cutoff_start_day: group.cutoff_start_day || 1,
                cutoff_end_day: group.cutoff_end_day || 0,
                is_active: group.is_active
            });
        } else {
            setEditingGroup(null);
            setFormData({
                group_code: '',
                group_name: '',
                description: '',
                cutoff_type: 'CALENDAR',
                cutoff_start_day: 1,
                cutoff_end_day: 0,
                is_active: 1
            });
        }
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingGroup(null);
    }, []);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleSubmit = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL1}/sm/payroll-groups${editingGroup ? `/${editingGroup.id}` : ''}`;
            const method = editingGroup ? 'put' : 'post';

            const res = await axios[method](url, formData, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });

            if (res.data.success) {
                fetchGroups();
                handleCloseDialog();
            }
        } catch (err) {
            console.error("Error saving group:", err);
            alert(err.response?.data?.message || "Failed to save group");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this payroll group?")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/payroll-groups/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                fetchGroups();
            }
        } catch (err) {
            console.error("Error deleting group:", err);
            alert(err.response?.data?.message || "Failed to delete group");
        }
    };

    // -----------------------------------------------------
    // Schedule Handlers
    // -----------------------------------------------------
    const handleOpenScheduleDialog = useCallback((group, schedule = null) => {
        setSelectedGroupForSchedule(group);
        if (schedule) {
            setEditingSchedule(schedule);
            setScheduleFormData({
                schedule_name: schedule.schedule_name,
                split_day: schedule.split_day,
                pay_basic: schedule.pay_basic,
                pay_ot: schedule.pay_ot,
                pay_allowances: schedule.pay_allowances,
                run_order: schedule.run_order,
                is_active: schedule.is_active
            });
        } else {
            setEditingSchedule(null);
            setScheduleFormData({
                schedule_name: '',
                split_day: 15,
                pay_basic: 1,
                pay_ot: 1,
                pay_allowances: 1,
                run_order: (group.schedules?.length || 0) + 1,
                is_active: 1
            });
        }
        setOpenScheduleDialog(true);
    }, []);

    const handleCloseScheduleDialog = useCallback(() => {
        setOpenScheduleDialog(false);
        setEditingSchedule(null);
        setSelectedGroupForSchedule(null);
    }, []);

    const handleScheduleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setScheduleFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleScheduleSubmit = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL1}/sm/payroll-groups/schedules${editingSchedule ? `/${editingSchedule.id}` : ''}`;
            const method = editingSchedule ? 'put' : 'post';

            const payload = { ...scheduleFormData, payroll_group_id: selectedGroupForSchedule.id };

            const res = await axios[method](url, payload, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });

            if (res.data.success) {
                fetchGroups();
                setSelectedSchedulesGroup(null);
                handleCloseScheduleDialog();
            }
        } catch (err) {
            console.error("Error saving schedule:", err);
            alert(err.response?.data?.message || "Failed to save schedule");
        }
    };

    const handleDeleteSchedule = async (id) => {
        if (!window.confirm("Are you sure you want to delete this advance schedule?")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/payroll-groups/schedules/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                fetchGroups();
                setSelectedSchedulesGroup(null);
            }
        } catch (err) {
            console.error("Error deleting schedule:", err);
            alert(err.response?.data?.message || "Failed to delete schedule");
        }
    };

    // -----------------------------------------------------
    // Columns
    // -----------------------------------------------------
    const groupColDefs = useMemo(() => [
        { field: "group_code", headerName: "Code", width: 120, pinned: 'left' },
        { field: "group_name", headerName: "Group Name", flex: 1, minWidth: 200 },
        {
            headerName: "Cutoff Period",
            width: 200,
            valueGetter: (p) => p.data.cutoff_type === 'CALENDAR' ? 'Calendar Month' : `Custom (Day ${p.data.cutoff_start_day})`
        },
        {
            headerName: "Advance Runs",
            width: 150,
            cellRenderer: (p) => (
                <Chip size="small" label={p.data.has_advance_runs ? `${p.data.schedules?.length || 0} Schedules` : 'None'}
                      color={p.data.has_advance_runs ? 'primary' : 'default'} />
            )
        },
        {
            field: "is_active", headerName: "Status", width: 120,
            cellRenderer: (p) => (
                <Chip size="small" label={p.value ? 'Active' : 'Inactive'} color={p.value ? 'success' : 'error'} />
            )
        },
        {
            headerName: "Actions",
            width: 180,
            sortable: false,
            filter: false,
            pinned: 'right',
            cellRenderer: (params) => {
                const isDefault = params.data.group_code === 'DEFAULT';
                return (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Manage Advance Schedules">
                            <IconButton size="small" color="info" onClick={() => setSelectedSchedulesGroup(params.data)}>
                                <ScheduleIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Group">
                            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.data)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {!isDefault && (
                            <Tooltip title="Delete Group">
                                <IconButton size="small" color="error" onClick={() => handleDelete(params.data.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                );
            }
        }
    ], [handleOpenDialog, handleOpenScheduleDialog]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Payroll Groups" setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight="bold">Payroll Groups</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add Group
                    </Button>
                </Stack>

                <Card sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
                    <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            rowData={groups}
                            columnDefs={groupColDefs}
                            defaultColDef={{ sortable: true, filter: true, resizable: true }}
                            rowSelection="single"
                            animateRows={true}
                            domLayout="normal"
                        />
                    </div>
                </Card>
            </Box>

            {/* GROUP DIALOG */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingGroup ? 'Edit Payroll Group' : 'Add Payroll Group'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Group Code" name="group_code" value={formData.group_code} onChange={handleChange} disabled={editingGroup?.group_code === 'DEFAULT'} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Group Name" name="group_name" value={formData.group_name} onChange={handleChange} required />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Cutoff Type</InputLabel>
                                <Select name="cutoff_type" value={formData.cutoff_type} onChange={handleChange} label="Cutoff Type">
                                    <MenuItem value="CALENDAR">Calendar Month (1st - End)</MenuItem>
                                    <MenuItem value="CUSTOM">Custom Cutoff</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {formData.cutoff_type === 'CUSTOM' && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth type="number" label="Cutoff Start Day" name="cutoff_start_day" value={formData.cutoff_start_day} onChange={handleChange} InputProps={{ inputProps: { min: 1, max: 28 } }} helperText="e.g. 21" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth type="number" label="Cutoff End Day" name="cutoff_end_day" value={formData.cutoff_end_day} onChange={handleChange} InputProps={{ inputProps: { min: 1, max: 28 } }} helperText="e.g. 20" />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <FormControlLabel control={<Checkbox checked={Boolean(formData.is_active)} onChange={handleChange} name="is_active" />} label="Active" />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(selectedSchedulesGroup)} onClose={() => setSelectedSchedulesGroup(null)} maxWidth="md" fullWidth>
                <DialogTitle>Advance Schedules — {selectedSchedulesGroup?.group_name}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        {(selectedSchedulesGroup?.schedules || []).map(schedule => (
                            <Card key={schedule.id} variant="outlined" sx={{ p: 1.5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography fontWeight={700}>{schedule.run_order}. {schedule.schedule_name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Through day {schedule.split_day} · {[schedule.pay_basic && 'Basic',schedule.pay_ot && 'OT',schedule.pay_allowances && 'Allowances'].filter(Boolean).join(', ') || 'No pay components'}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row">
                                        <IconButton color="primary" onClick={() => handleOpenScheduleDialog(selectedSchedulesGroup,schedule)}><EditIcon /></IconButton>
                                        <IconButton color="error" onClick={() => handleDeleteSchedule(schedule.id)}><DeleteIcon /></IconButton>
                                    </Stack>
                                </Stack>
                            </Card>
                        ))}
                        {!(selectedSchedulesGroup?.schedules || []).length && <Typography color="text.secondary">No advance schedules configured.</Typography>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedSchedulesGroup(null)}>Close</Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenScheduleDialog(selectedSchedulesGroup)}>Add Schedule</Button>
                </DialogActions>
            </Dialog>

            {/* SCHEDULE DIALOG */}
            <Dialog open={openScheduleDialog} onClose={handleCloseScheduleDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingSchedule ? 'Edit Advance Schedule' : 'Add Advance Schedule'}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Adding to group: {selectedGroupForSchedule?.group_name}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField fullWidth label="Schedule Name" name="schedule_name" value={scheduleFormData.schedule_name} onChange={handleScheduleChange} required placeholder="e.g. Mid-Month Advance" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth type="number" label="Cutoff Day" name="split_day" value={scheduleFormData.split_day} onChange={handleScheduleChange} required helperText="Day of month" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" label="Run Order" name="run_order" value={scheduleFormData.run_order} onChange={handleScheduleChange} required />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>Calculation Includes:</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControlLabel control={<Checkbox checked={Boolean(scheduleFormData.pay_basic)} onChange={handleScheduleChange} name="pay_basic" />} label="Prorated Basic" />
                                <FormControlLabel control={<Checkbox checked={Boolean(scheduleFormData.pay_ot)} onChange={handleScheduleChange} name="pay_ot" />} label="Actual OT" />
                                <FormControlLabel control={<Checkbox checked={Boolean(scheduleFormData.pay_allowances)} onChange={handleScheduleChange} name="pay_allowances" />} label="Allowances" />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Checkbox checked={Boolean(scheduleFormData.is_active)} onChange={handleScheduleChange} name="is_active" />} label="Active" />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleScheduleSubmit}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PayrollGroups;
